import { For, Show } from "solid-js";
import { Pencil, Sparkles, Trash2, X } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, GlassSurface, Tooltip, TooltipContent, TooltipTrigger } from "@fleet/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@time-tracker/shared";
import type { Project } from "@time-tracker/powersync";
import { useEntrySwipeGestures } from "@/features/chronos-sync/hooks/use-entry-swipe-gestures";

export interface TimelineRowItem {
  id: string;
  type: "registered" | "unassigned" | "event";
  taskName?: string;
  tags?: { id: string; name: string; color: string }[];
  projectName: string;
  projectCode: string;
  projectColor: string;
  description: string;
  startTime: string;
  endTime: string;
  duration: string;
  rawStartTime: string;
  appClassList?: { name: string; duration: string; letter: string }[];
  logIds?: string[];
}

interface TimelineRowProps {
  item: TimelineRowItem;
  projects: Project[];
  isPopoverOpen: () => boolean;
  setPopoverOpen: (open: boolean) => void;
  getDotColor: (color: string) => string;
  appColorClass: (idx: number) => string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDismiss: (logIds: string[]) => void;
  onAssign: (item: TimelineRowItem, projectId: string) => void;
}

const COMMIT_TRANSITION = "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * One timeline entry, with swipe-to-action on touch:
 *   - registered entries: swipe right = edit, swipe left = delete,
 *   - unassigned activity blocks: swipe left = dismiss.
 * The visible Edit/Delete/Dismiss buttons stay as the keyboard/desktop path; the
 * swipe just invokes the exact same handlers, so a11y and behaviour are intact.
 */
export function TimelineRow(props: TimelineRowProps) {
  const { t } = useI18n();

  let surfaceRef: HTMLDivElement | undefined;

  const canSwipeLeft = () =>
    props.item.type === "registered" ||
    (props.item.type === "unassigned" && Boolean(props.item.logIds));
  const canSwipeRight = () => props.item.type === "registered";

  const swipe = useEntrySwipeGestures({
    canSwipeLeft,
    canSwipeRight,
    onSwipeLeft: () => {
      if (props.item.type === "registered") {
        props.onDelete(props.item.id);
      } else if (props.item.logIds) {
        props.onDismiss(props.item.logIds);
      }
    },
    onSwipeRight: () => {
      if (props.item.type === "registered") {
        props.onEdit(props.item.id);
      }
    },
    getSurfaceRef: () => surfaceRef,
  });

  const swipeEnabled = () => canSwipeLeft() || canSwipeRight();

  return (
    <div class="relative group">
      {/* Timeline tree node dot indicator */}
      <div
        class={cn(
          "absolute -left-[21.5px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-black transition-[background-color,box-shadow] duration-300",
          props.item.type === "registered" && "bg-primary shadow-[0_0_8px_var(--glow-0)]",
          props.item.type === "event" && "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.4)]",
          props.item.type === "unassigned" && "bg-muted-foreground/45 animate-pulse",
        )}
      />

      {/* Swipe clip wrapper: data-no-swipe so the global page-swipe yields here. */}
      <div
        class="relative overflow-hidden rounded-[24px] touch-pan-y"
        data-no-swipe={swipeEnabled() ? "true" : undefined}
      >
        {/* Action underlay revealed as the surface slides. */}
        <Show when={swipeEnabled()}>
          <div
            class="absolute inset-0 flex items-center justify-between px-6 rounded-[24px] pointer-events-none"
            aria-hidden="true"
          >
            <span
              class={cn(
                "flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider transition-opacity duration-150",
                props.item.type === "registered" ? "text-[#afc6ff]" : "text-transparent",
                swipe.tx() > 0 ? "opacity-100" : "opacity-0",
              )}
            >
              <Pencil class="h-4 w-4" />
              {t(msg`Edit`)}
            </span>
            <span
              class={cn(
                "flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-destructive transition-opacity duration-150",
                swipe.tx() < 0 ? "opacity-100" : "opacity-0",
              )}
            >
              {props.item.type === "registered" ? t(msg`Delete`) : t(msg`Dismiss`)}
              {props.item.type === "registered" ? (
                <Trash2 class="h-4 w-4" />
              ) : (
                <X class="h-4 w-4" />
              )}
            </span>
          </div>
        </Show>

        <GlassSurface
          ref={(el) => {
            surfaceRef = el;
          }}
          class={cn(
            "relative z-10 p-5 space-y-4 bg-[#121212] border border-white/10 rounded-[24px] transition-colors duration-300 hover:border-white/20 shadow-none",
            props.item.type === "unassigned" && "shadow-[0_0_24px_var(--glow-0)]",
          )}
          style={{
            transform: `translate3d(${swipe.tx()}px, 0, 0)`,
            transition: swipe.isAnimating() ? COMMIT_TRANSITION : "none",
          }}
          onPointerDown={swipe.handlePointerDown}
          onPointerMove={swipe.handlePointerMove}
          onPointerUp={swipe.handlePointerUp}
          onPointerCancel={swipe.handlePointerCancel}
        >
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Project identity */}
            <div class="flex items-center gap-2.5">
              <span
                class={cn(
                  "w-2 h-2 rounded-full shrink-0",
                  props.getDotColor(props.item.projectColor),
                )}
              />
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-bold text-foreground font-mono truncate">
                    {props.item.projectName}
                  </span>
                  <Badge
                    variant="outline"
                    class="text-[9px] font-mono font-bold uppercase tracking-wider bg-white/5 border-white/10 px-1.5 py-0 shrink-0"
                  >
                    {props.item.projectCode}
                  </Badge>
                </div>
                <span class="text-[10px] text-muted-foreground font-mono mt-0.5 block">
                  {props.item.startTime === props.item.endTime
                    ? props.item.startTime
                    : `${props.item.startTime} - ${props.item.endTime}`}{" "}
                  ({props.item.duration})
                </span>
              </div>
            </div>

            {/* Display Status Badges */}
            <div class="flex items-center gap-2">
              <Show when={props.item.type === "registered"}>
                <Badge class="bg-primary/10 text-primary border border-primary/20 text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5">
                  {t(msg`Logged`)}
                </Badge>
              </Show>
              <Show when={props.item.type === "event"}>
                <Badge class="bg-amber-400/10 text-amber-400 border border-amber-400/20 text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5">
                  {t(msg`Event Note`)}
                </Badge>
              </Show>
              <Show when={props.item.type === "unassigned"}>
                <Badge class="bg-primary/20 text-primary border border-primary/30 text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 animate-pulse">
                  {t(msg`Pending Log`)}
                </Badge>
              </Show>
            </div>
          </div>

          {/* App stacks and tooltips */}
          <Show when={props.item.appClassList && props.item.appClassList.length > 0}>
            <div class="flex items-center gap-3.5 bg-[#0e0e0e] p-3.5 rounded-[16px] border border-white/5">
              <div class="flex items-center">
                <For each={props.item.appClassList}>
                  {(app, idx) => (
                    <Tooltip>
                      <TooltipTrigger>
                        <div
                          class={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center font-mono text-xs font-extrabold border -ml-2.5 first:ml-0 transition-transform will-change-transform hover:-translate-y-1 hover:z-20 cursor-help shadow-sm",
                            props.appColorClass(idx()),
                          )}
                        >
                          {app.letter}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent class="bg-[#1a1a1a]/95 backdrop-blur-md border border-white/10 text-white px-2.5 py-1.5 rounded-[8px] text-[10px] font-mono">
                        <span class="font-bold">{app.name}</span>: {app.duration}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </For>
              </div>

              {/* Textual Description of Apps */}
              <div class="flex flex-col gap-0.5 min-w-0">
                <span class="text-[9px] font-bold text-muted-foreground uppercase tracking-widest font-mono">
                  {t(msg`Applications Tracked`)}
                </span>
                <p class="text-xs text-foreground/80 truncate">
                  {(props.item.appClassList ?? [])
                    .map((a) => `${a.name} (${a.duration})`)
                    .join(", ")}
                </p>
              </div>
            </div>
          </Show>

          {/* Details / Window Title */}
          <div class="text-xs text-muted-foreground/80 leading-relaxed font-mono">
            <Show when={props.item.taskName}>
              <span class="text-[#ceffdf] mr-1.5">[{props.item.taskName}]</span>
            </Show>
            {props.item.description}
          </div>

          {/* Tag chips */}
          <Show when={(props.item.tags ?? []).length > 0}>
            <div class="flex flex-wrap items-center gap-1.5">
              <For each={props.item.tags}>
                {(tagChip) => (
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono"
                    style={{
                      "border-color": `${tagChip.color}40`,
                      color: tagChip.color,
                      "background-color": `${tagChip.color}14`,
                    }}
                  >
                    {tagChip.name}
                  </span>
                )}
              </For>
            </div>
          </Show>

          {/* Acciones para registros propios */}
          <Show when={props.item.type === "registered"}>
            <div
              class="flex items-center gap-2 justify-end pt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              data-no-entry-swipe="true"
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  props.onEdit(props.item.id);
                }}
                class="h-7 border-white/10 text-muted-foreground hover:text-foreground flex items-center gap-1.5 cursor-pointer rounded-xl font-mono text-[10px] uppercase tracking-wider"
              >
                <Pencil class="h-3 w-3" />
                {t(msg`Edit`)}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  props.onDelete(props.item.id);
                }}
                class="h-7 border-destructive/20 text-destructive hover:bg-destructive/10 hover:text-destructive flex items-center gap-1.5 cursor-pointer rounded-xl font-mono text-[10px] uppercase tracking-wider"
              >
                <Trash2 class="h-3 w-3" />
                {t(msg`Delete`)}
              </Button>
            </div>
          </Show>

          {/* Action buttons (only for unassigned logs) */}
          <Show when={props.item.type === "unassigned" && props.item.logIds}>
            <div class="flex items-center gap-3 justify-end pt-1" data-no-entry-swipe="true">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const ids = props.item.logIds;
                  if (ids) {
                    props.onDismiss(ids);
                  }
                }}
                class="h-8 border-destructive/20 text-destructive hover:bg-destructive/10 hover:text-destructive flex items-center gap-1.5 cursor-pointer rounded-xl font-mono text-[10px] uppercase tracking-wider"
              >
                <X class="h-3.5 w-3.5" />
                {t(msg`Dismiss`)}
              </Button>

              <Popover open={props.isPopoverOpen()} onOpenChange={props.setPopoverOpen}>
                <PopoverTrigger>
                  <Button
                    size="sm"
                    class="h-8 bg-primary text-primary-foreground hover:bg-primary/95 flex items-center gap-1.5 cursor-pointer rounded-xl font-mono text-[10px] uppercase tracking-wider"
                  >
                    <Sparkles class="h-3.5 w-3.5" />
                    {t(msg`Log Time...`)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent class="bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-[20px] p-3 shadow-2xl w-60 z-50">
                  <span class="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block mb-2 font-mono">
                    {t(msg`Assign Project`)}
                  </span>
                  <div class="space-y-1">
                    <For each={props.projects}>
                      {(proj) => (
                        <button
                          type="button"
                          onClick={() => {
                            props.onAssign(props.item, proj.id);
                          }}
                          class="w-full flex items-center justify-between p-2 rounded-xl text-left hover:bg-white/5 transition-colors group cursor-pointer text-xs font-mono"
                        >
                          <div class="flex items-center gap-2 min-w-0">
                            <span
                              class={cn(
                                "w-2 h-2 rounded-full shrink-0",
                                props.getDotColor(proj.color ?? ""),
                              )}
                            />
                            <span class="text-foreground font-bold truncate">{proj.name}</span>
                          </div>
                          <span class="text-[9px] text-muted-foreground font-mono uppercase tracking-wider border border-white/10 px-1.5 py-0.5 rounded-lg">
                            {proj.code}
                          </span>
                        </button>
                      )}
                    </For>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </Show>
        </GlassSurface>
      </div>
    </div>
  );
}
